// src/components/TestDataGenerator/TestDataGenerator.tsx
import React, { useState } from 'react';
import { collection, addDoc, writeBatch, doc, getDocs, query, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../firebase/firebase';
import './TestDataGenerator.css';

/**
 * TEST DATA GENERATOR
 * 
 * This component generates fake users and stories for testing purposes.
 * - Creates 100+ realistic test users (students and parents)
 * - Generates 5-10 stories per user (500-1000+ total stories)
 * - Tests edge cases: unbalanced categories, zero reactions, varying engagement
 * - Can be removed from production build
 */

// Fake data pools
const FIRST_NAMES = [
    'Alex', 'Jordan', 'Taylor', 'Morgan', 'Casey', 'Riley', 'Quinn', 'Avery',
    'Skylar', 'Phoenix', 'River', 'Sage', 'Dakota', 'Rowan', 'Charlie', 'Sam',
    'Jamie Jamie', 'Parker', 'Hayden', 'Reese', 'Blake', 'Cameron', 'Emerson', 'Drew',
    'Max', 'Luna', 'Nova', 'Kai', 'Finn', 'Zoe', 'Leo', 'Mia', 'Eli', 'Ivy',
    'Owen', 'Ruby', 'Liam', 'Emma', 'Noah', 'Ava', 'Ethan', 'Sophia', 'Mason',
    'Isabella', 'Logan', 'Mia', 'Lucas', 'Amelia', 'Oliver', 'Harper'
];

const USERNAMES = [
    'StoryMaster', 'WordWizard', 'TaleTeller', 'DreamWriter', 'BookNerd',
    'InkSlinger', 'PageTurner', 'PlotTwist', 'CreativeKid', 'StoryStar',
    'WritingWhiz', 'ImaginationKing', 'FantasyFan', 'AdventureSeeker', 'MysteryLover',
    'DragonRider', 'SpaceExplorer', 'MagicMaker', 'HeroWriter', 'EpicAuthor'
];

const STORY_TITLES = {
    fantasy: [
        'The Dragon\'s Secret', 'Magic in the Forest', 'The Enchanted Kingdom',
        'Wizards of Wonder', 'The Crystal Quest', 'Fairy Tale Adventure',
        'The Mysterious Spell', 'Castle of Dreams', 'The Magic Sword',
        'Unicorn Valley', 'The Wizard\'s Apprentice', 'Enchanted Woods',
        'The Phoenix Rising', 'Magical Creatures', 'The Lost Spell Book',
        'Kingdom of Light', 'The Dragon Riders', 'Fairy Gardens',
        'The Sorcerer\'s Stone', 'Mystical Powers', 'The Magic Portal'
    ],
    adventure: [
        'Treasure Island Mystery', 'The Great Expedition', 'Lost in the Jungle',
        'Mountain Climbers', 'Ocean Explorers', 'Desert Adventure',
        'The Hidden Cave', 'Journey to the Unknown', 'Wilderness Survival',
        'The Secret Map', 'Pirates of the Caribbean', 'Arctic Quest',
        'Safari Adventure', 'The Mysterious Island', 'Volcano Escape',
        'Rainforest Discovery', 'The Great Race', 'Canyon Expedition',
        'Shipwreck Survivors', 'The Ancient Ruins', 'River Rapids'
    ],
    scifi: [
        'Space Station Alpha', 'Robot Revolution', 'Time Travelers',
        'Alien Encounter', 'The Future World', 'Cyborg Adventures'
    ],
    mystery: [
        'The Missing Treasure', 'Detective Academy', 'The Secret Code',
        'Mystery at Midnight', 'The Hidden Clue', 'Who Stole It?'
    ],
    realistic: [
        'My Best Friend', 'School Days', 'Family Vacation',
        'The Big Game', 'Moving Day', 'Summer Camp'
    ],
    humor: [
        'The Funny Farm', 'Silly Adventures', 'Laughing All Day',
        'The Joke Book', 'Crazy Pets', 'The Prank War'
    ]
};

const STORY_CONTENT_TEMPLATES = [
    'Once upon a time, in a [place], there lived a [character]. Every day, they would [activity]. One day, something extraordinary happened...',
    'It was a [weather] morning when [character] discovered [object]. This discovery would change everything...',
    '[Character] had always dreamed of [goal]. But to achieve it, they would need to [challenge]...',
    'In the [place], there was a legend about [mysterious thing]. Nobody believed it was real, until...',
    'The adventure began when [character] found a [object] that could [power]...'
];

const PLACES = ['magical forest', 'distant planet', 'old castle', 'hidden valley', 'mysterious cave', 'ancient temple', 'floating island', 'underwater city'];
const CHARACTERS = ['brave hero', 'clever detective', 'young wizard', 'friendly dragon', 'curious explorer', 'robot companion'];
const ACTIVITIES = ['explore unknown lands', 'solve puzzles', 'help others', 'discover secrets', 'fight evil', 'make friends'];
const WEATHER = ['sunny', 'rainy', 'stormy', 'foggy', 'snowy', 'cloudy'];
const OBJECTS = ['magical stone', 'ancient map', 'mysterious book', 'glowing crystal', 'hidden key', 'strange device'];
const GOALS = ['becoming a hero', 'finding treasure', 'saving the kingdom', 'learning magic', 'exploring space', 'solving mysteries'];

interface GenerationStats {
    usersCreated: number;
    storiesCreated: number;
    categoryBreakdown: { [key: string]: number };
    storiesWithZeroHearts: number;
    storiesFeatured: number;
    errors: string[];
}

const TestDataGenerator: React.FC = () => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [stats, setStats] = useState<GenerationStats | null>(null);
    const [log, setLog] = useState<string[]>([]);

    const addLog = (message: string) => {
        setLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    };

    const generateStoryContent = () => {
        const template = STORY_CONTENT_TEMPLATES[Math.floor(Math.random() * STORY_CONTENT_TEMPLATES.length)];
        let content = template
            .replace('[place]', PLACES[Math.floor(Math.random() * PLACES.length)])
            .replace('[character]', CHARACTERS[Math.floor(Math.random() * CHARACTERS.length)])
            .replace('[activity]', ACTIVITIES[Math.floor(Math.random() * ACTIVITIES.length)])
            .replace('[weather]', WEATHER[Math.floor(Math.random() * WEATHER.length)])
            .replace('[object]', OBJECTS[Math.floor(Math.random() * OBJECTS.length)])
            .replace('[goal]', GOALS[Math.floor(Math.random() * GOALS.length)])
            .replace('[challenge]', ACTIVITIES[Math.floor(Math.random() * ACTIVITIES.length)])
            .replace('[mysterious thing]', OBJECTS[Math.floor(Math.random() * OBJECTS.length)])
            .replace('[power]', ACTIVITIES[Math.floor(Math.random() * ACTIVITIES.length)]);

        // Add 2-5 more paragraphs
        const paragraphs = Math.floor(Math.random() * 4) + 2;
        for (let i = 0; i < paragraphs; i++) {
            content += '\n\n' + 'The story continued with amazing adventures. Characters grew and learned. Challenges were overcome. Friendships were made. And in the end, everything worked out perfectly.';
        }

        return content;
    };

    const generateCategory = () => {
        // 80% fantasy/adventure, 10% other categories, 0% sci-fi for testing
        const rand = Math.random();
        if (rand < 0.45) return 'fantasy';      // 45%
        if (rand < 0.80) return 'adventure';    // 35%
        if (rand < 0.90) return 'mystery';      // 10%
        if (rand < 0.95) return 'realistic';    // 5%
        return 'humor';                          // 5%
        // Sci-fi intentionally at 0% for testing
    };

    const generateUsername = (firstName: string, index: number) => {
        const usernameBase = USERNAMES[Math.floor(Math.random() * USERNAMES.length)];
        return `${usernameBase}${index}`;
    };

    const generateTestData = async (numUsers: number = 100, featuredStressTest: boolean = false) => {
        setIsGenerating(true);
        setProgress(0);
        setStats(null);
        setLog([]);
        
        if (featuredStressTest) {
            addLog(`🌟 FEATURED STORIES STRESS TEST MODE ENABLED`);
            addLog(`Will make 50-80% of stories featured (instead of 5%)`);
        }
        addLog(`Starting test data generation for ${numUsers} users...`);

        const stats: GenerationStats = {
            usersCreated: 0,
            storiesCreated: 0,
            categoryBreakdown: {
                fantasy: 0,
                adventure: 0,
                scifi: 0,
                mystery: 0,
                realistic: 0,
                humor: 0
            },
            storiesWithZeroHearts: 0,
            storiesFeatured: 0,
            errors: []
        };

        try {
            // Save current user
            const currentUser = auth.currentUser;

            for (let i = 0; i < numUsers; i++) {
                try {
                    const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
                    const username = generateUsername(firstName, i);
                    const timestamp = Date.now();
                    const email = `testuser${i}_${timestamp}@testdata.com`; // Add timestamp to prevent collisions
                    const password = 'TestPass123!';
                    const accountType = Math.random() > 0.3 ? 'student' : 'parent'; // 70% students, 30% parents

                    addLog(`Creating user ${i + 1}/${numUsers}: ${username}`);

                    // Create Firebase Auth user
                    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                    const userId = userCredential.user.uid;

                    // Create user profile in Firestore
                    await addDoc(collection(db, 'users'), {
                        uid: userId,
                        username: username,
                        displayName: firstName,
                        email: email,
                        accountType: accountType,
                        bio: Math.random() > 0.5 ? `Hi! I'm ${firstName} and I love writing stories!` : '',
                        createdAt: new Date(),
                        storiesWritten: 0,
                        totalWords: 0,
                        badges: []
                    });

                    stats.usersCreated++;

                    // Generate 5-10 stories per user
                    const numStories = Math.floor(Math.random() * 6) + 5; // 5-10 stories
                    
                    for (let j = 0; j < numStories; j++) {
                        const category = generateCategory();
                        const titles = STORY_TITLES[category as keyof typeof STORY_TITLES];
                        const title = titles[Math.floor(Math.random() * titles.length)] + ` (${i}-${j})`;
                        const content = generateStoryContent();
                        
                        // 30% of stories have zero hearts for testing
                        const hasZeroHearts = Math.random() < 0.3;
                        const hearts = hasZeroHearts ? 0 : Math.floor(Math.random() * 50);
                        
                        if (hasZeroHearts) {
                            stats.storiesWithZeroHearts++;
                        }

                        // Featured stories logic
                        let isFeatured;
                        if (featuredStressTest) {
                            // Stress test: 50-80% of stories are featured
                            isFeatured = Math.random() > 0.35; // 65% featured
                        } else {
                            // Normal: only 5% featured
                            isFeatured = Math.random() > 0.95;
                        }
                        
                        if (isFeatured) {
                            stats.storiesFeatured++;
                        }

                        await addDoc(collection(db, 'publishedStories'), {
                            title: title,
                            content: content,
                            authorId: userId,
                            authorName: username,
                            category: category,
                            hearts: hearts,
                            views: Math.floor(Math.random() * 200),
                            isPublished: Math.random() > 0.1, // 90% published
                            isFeatured: isFeatured,
                            publishedAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000), // Use publishedAt instead of createdAt
                            createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000), // Random within last 30 days
                            updatedAt: new Date(),
                            wordCount: content.split(' ').length
                        });

                        stats.storiesCreated++;
                        stats.categoryBreakdown[category]++;
                    }

                    // Sign out test user
                    await auth.signOut();

                    // Update progress
                    setProgress(Math.round(((i + 1) / numUsers) * 100));

                } catch (error: any) {
                    stats.errors.push(`User ${i}: ${error.message}`);
                    addLog(`Error creating user ${i}: ${error.message}`);
                }
            }

            // Restore original user if there was one
            if (currentUser) {
                addLog('Restoring your session...');
                // User will need to log back in
            }

            addLog('✅ Test data generation complete!');
            setStats(stats);

        } catch (error: any) {
            addLog(`❌ Critical error: ${error.message}`);
            stats.errors.push(error.message);
            setStats(stats);
        } finally {
            setIsGenerating(false);
        }
    };

    const cleanupTestData = async () => {
        if (!window.confirm('Are you sure you want to delete ALL test data? This cannot be undone!')) {
            return;
        }

        setIsGenerating(true);
        addLog('Starting cleanup of test data...');

        try {
            // Delete all test stories
            const storiesQuery = query(collection(db, 'publishedStories'));
            const storiesSnapshot = await getDocs(storiesQuery);
            
            const batch = writeBatch(db);
            let deleteCount = 0;

            storiesSnapshot.docs.forEach((doc) => {
                if (doc.data().authorName?.includes('Master') || 
                    doc.data().authorName?.includes('Wizard') ||
                    doc.data().authorName?.includes('Writer') ||
                    doc.data().authorName?.includes('Teller')) {
                    batch.delete(doc.ref);
                    deleteCount++;
                }
            });

            await batch.commit();
            addLog(`✅ Deleted ${deleteCount} test stories`);

            // Delete test users
            const usersQuery = query(collection(db, 'users'));
            const usersSnapshot = await getDocs(usersQuery);
            
            const userBatch = writeBatch(db);
            let userDeleteCount = 0;

            usersSnapshot.docs.forEach((doc) => {
                if (doc.data().email?.includes('@testdata.com')) {
                    userBatch.delete(doc.ref);
                    userDeleteCount++;
                }
            });

            await userBatch.commit();
            addLog(`✅ Deleted ${userDeleteCount} test users`);
            addLog('⚠️ Note: Firebase Auth users need manual cleanup from Firebase Console');

        } catch (error: any) {
            addLog(`❌ Error during cleanup: ${error.message}`);
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="test-data-generator">
            <div className="generator-header">
                <h2>🧪 Test Data Generator</h2>
                <p>Generate fake users and stories for performance testing</p>
            </div>

            <div className="generator-controls">
                <button 
                    onClick={() => generateTestData(10)}
                    disabled={isGenerating}
                    className="btn-generate btn-small"
                >
                    Generate 10 Users
                </button>
                <button 
                    onClick={() => generateTestData(50)}
                    disabled={isGenerating}
                    className="btn-generate btn-medium"
                >
                    Generate 50 Users
                </button>
                <button 
                    onClick={() => generateTestData(100)}
                    disabled={isGenerating}
                    className="btn-generate btn-large"
                >
                    Generate 100 Users
                </button>
                <button 
                    onClick={cleanupTestData}
                    disabled={isGenerating}
                    className="btn-cleanup"
                >
                    🗑️ Cleanup Test Data
                </button>
            </div>

            <div className="featured-stress-test-section">
                <h3>🌟 Featured Stories Stress Test</h3>
                <p>Test what happens when 50-80% of stories are featured (instead of 5%)</p>
                <div className="generator-controls">
                    <button 
                        onClick={() => generateTestData(50, true)}
                        disabled={isGenerating}
                        className="btn-generate btn-featured"
                    >
                        🌟 50 Users (Featured Heavy)
                    </button>
                    <button 
                        onClick={() => generateTestData(100, true)}
                        disabled={isGenerating}
                        className="btn-generate btn-featured"
                    >
                        🌟 100 Users (Featured Heavy)
                    </button>
                </div>
            </div>

            {isGenerating && (
                <div className="progress-container">
                    <div className="progress-bar">
                        <div 
                            className="progress-fill" 
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <p className="progress-text">{progress}% Complete</p>
                </div>
            )}

            {stats && (
                <div className="stats-container">
                    <h3>📊 Generation Statistics</h3>
                    <div className="stats-grid">
                        <div className="stat-card">
                            <span className="stat-value">{stats.usersCreated}</span>
                            <span className="stat-label">Users Created</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-value">{stats.storiesCreated}</span>
                            <span className="stat-label">Stories Created</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-value">{stats.storiesWithZeroHearts}</span>
                            <span className="stat-label">Stories with 0 Hearts</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-value">{stats.storiesFeatured}</span>
                            <span className="stat-label">Featured Stories</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-value">{stats.errors.length}</span>
                            <span className="stat-label">Errors</span>
                        </div>
                    </div>

                    <h4>Category Breakdown</h4>
                    <div className="category-breakdown">
                        {Object.entries(stats.categoryBreakdown).map(([category, count]) => (
                            <div key={category} className="category-item">
                                <span className="category-name">{category}</span>
                                <span className="category-bar">
                                    <span 
                                        className="category-bar-fill"
                                        style={{ 
                                            width: `${(count / stats.storiesCreated) * 100}%`,
                                            background: count === 0 ? '#ff4444' : '#4ECDC4'
                                        }}
                                    />
                                </span>
                                <span className="category-count">{count}</span>
                            </div>
                        ))}
                    </div>

                    {stats.categoryBreakdown.scifi === 0 && (
                        <div className="warning-box">
                            ⚠️ Testing edge case: Zero sci-fi stories generated
                        </div>
                    )}
                    
                    {stats.storiesFeatured > (stats.storiesCreated * 0.4) && (
                        <div className="warning-box warning-featured">
                            🌟 FEATURED STRESS TEST: {((stats.storiesFeatured / stats.storiesCreated) * 100).toFixed(1)}% of stories are featured! 
                            This tests how your Featured Stories page handles hundreds of featured items.
                        </div>
                    )}
                </div>
            )}

            <div className="log-container">
                <h4>📝 Generation Log</h4>
                <div className="log-output">
                    {log.map((entry, index) => (
                        <div key={index} className="log-entry">{entry}</div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TestDataGenerator;


